/**
 * Persisted widget state — WHAT the widget keeps in the browser and HOW it is namespaced (review
 * finding M-09 residual).
 *
 * The widget caches the saved-chat list (`localStorage`) and the user's model pick. On a shared
 * browser a DIFFERENT person signing in must start from an empty slate, so every persisted key is
 * namespaced by a host-supplied identity (`userKey` prop, else `user.id`, else `user.email`); a
 * host that supplies none gets the shared `'anon'` bucket, which the host's sign-out hook wipes.
 * Nothing sensitive is written under a key another identity reads.
 *
 * Deliberately NOT namespaced: the voice/detailed mode toggle — a device-level UI preference that
 * carries no user data.
 *
 * Pure helpers (structural `StorageLike`) so the key derivation and the wipe are unit-testable.
 */
/** Bucket used when the host supplies no identity at all. Cleared by the sign-out hook like any other. */
export const ANONYMOUS_USER_KEY = 'anon';
/**
 * Resolve the identity key. Precedence: explicit `userKey` → `user.id` → `user.email` → `'anon'`.
 * Blank/whitespace values are skipped; the chosen value is trimmed but otherwise used verbatim so keys
 * written by earlier widget versions (`<app>-gyro-sessions-<id>`) keep resolving for the same person.
 */
export function persistedUserKey(user, explicit) {
    for (const candidate of [explicit, user?.id, user?.email]) {
        if (typeof candidate === 'string' && candidate.trim().length > 0)
            return candidate.trim();
    }
    return ANONYMOUS_USER_KEY;
}
/**
 * The desktop's notification list (notificationCenter.ts): written only for appKey "desktop", but removed on every sign-out
 * (the panel's own, and so each entry's). Here, beside the other keys, so the web entry need not carry the list's code.
 */
export const notificationCenterKey = (app, user) => `${app}-gyro-notifications-${user}`;
export function storageKeys(appKey, userKey) {
    return {
        sessions: `${appKey}-gyro-sessions-${userKey}`,
        model: `${appKey}-gyro-model-${userKey}`,
        legacyModel: `${appKey}-gyro-model`,
        look: `${appKey}-gyro-look-${userKey}`,
        layout: `${appKey}-gyro-layout-${userKey}`,
        payChats: `${appKey}-gyro-pay-chats-${userKey}`,
    };
}
/** The most chat ids remembered as holding pay data; the oldest go first. */
export const PAY_CHATS_MAX = 500;
/** The chat ids remembered as holding pay data: an empty set for anything missing, malformed or unreadable. Never throws. */
export function readPayChats(storage, key) {
    if (!storage)
        return new Set();
    try {
        const raw = storage.getItem(key);
        const list = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(list) ? list.filter((id) => typeof id === 'string' && id.length > 0 && id.length <= 200) : []);
    }
    catch {
        return new Set();
    }
}
/** Remember a chat as holding pay data (the newest last, at most PAY_CHATS_MAX). Never throws. */
export function rememberPayChat(storage, key, id) {
    if (!storage || !id)
        return;
    try {
        const ids = [...readPayChats(storage, key)].filter((known) => known !== id);
        ids.push(id);
        storage.setItem(key, JSON.stringify(ids.slice(-PAY_CHATS_MAX)));
    }
    catch {
        /* best effort: the chat is still marked in this session and by the service's history */
    }
}
/** The remembered layout; `{ expanded: false }` for anything missing, malformed or unreadable. Never throws. */
export function readFloatingLayout(storage, key) {
    if (!storage)
        return { expanded: false };
    try {
        const raw = storage.getItem(key);
        if (!raw)
            return { expanded: false };
        const record = JSON.parse(raw);
        return { expanded: record !== null && typeof record === 'object' && record.v === 1 && record.expanded === true };
    }
    catch {
        return { expanded: false };
    }
}
/**
 * Remember the layout. Only Expand is written; shrinking removes the key, so a browser keeps nothing for a person who never
 * expanded (a host's own sign-out sweep may not know this key). Best effort: false when there is no storage or it refused
 * (quota, privacy mode). Never throws.
 */
export function writeFloatingLayout(storage, key, value) {
    if (!storage)
        return false;
    try {
        if (value.expanded === true)
            storage.setItem(key, JSON.stringify({ v: 1, expanded: true }));
        else
            storage.removeItem(key);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Wipe everything the widget persisted for this app + identity (the host's sign-out hook). Best
 * effort per key — a storage that throws (quota, privacy mode, sandbox) never aborts the rest. Returns
 * the keys that were removed, for tests/diagnostics.
 */
export function clearPersistedState(storage, appKey, userKey) {
    if (!storage)
        return [];
    const keys = storageKeys(appKey, userKey);
    const removed = [];
    for (const key of [keys.sessions, keys.model, keys.legacyModel, keys.look, keys.layout, keys.payChats]) {
        try {
            storage.removeItem(key);
            removed.push(key);
        }
        catch {
            /* best effort */
        }
    }
    return removed;
}
//# sourceMappingURL=persistedState.js.map